import { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Trash, Warning } from '@phosphor-icons/react';

/**
 * useConfirmDialog — hook that exposes a `confirm(opts)` function returning a Promise<bool>.
 * Designed for one-off destructive confirmations (delete user, delete contact, delete cost row…).
 * Replaces window.confirm() with a brand-styled modal that aligns with the dark theme.
 *
 * Usage:
 *   const { ConfirmDialog, confirm } = useConfirmDialog();
 *   const ok = await confirm({ title: 'Je i sigurt?', description: '...', confirmText: 'Fshi' });
 *   if (ok) doDelete();
 *   return <>{otherJsx}<ConfirmDialog /></>;
 */
export function useConfirmDialog() {
  const [state, setState] = useState({
    open: false,
    title: '',
    description: '',
    confirmText: 'OK',
    cancelText: 'Cancel',
    danger: true,
    resolver: null,
  });

  const confirm = useCallback(({
    title = 'Are you sure?',
    description = 'This action cannot be undone.',
    confirmText = 'Delete',
    cancelText = 'Cancel',
    danger = true,
  } = {}) => {
    return new Promise((resolve) => {
      setState({ open: true, title, description, confirmText, cancelText, danger, resolver: resolve });
    });
  }, []);

  const handleClose = (decision) => {
    state.resolver?.(decision);
    setState((s) => ({ ...s, open: false, resolver: null }));
  };

  const ConfirmDialog = useCallback(() => (
    <Dialog open={state.open} onOpenChange={(o) => !o && handleClose(false)}>
      <DialogContent
        className={`bg-[#121212] max-w-sm mx-auto ${state.danger ? 'border-red-500/40' : 'border-[#0ABAB5]/40'}`}
        data-testid="confirm-dialog"
      >
        <DialogHeader>
          <div className={`w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-2 ${state.danger ? 'bg-red-500/15 text-red-400' : 'bg-[#0ABAB5]/15 text-[#0ABAB5]'}`}>
            {state.danger ? <Trash size={28} weight="fill" /> : <Warning size={28} weight="fill" />}
          </div>
          <DialogTitle className="text-center text-lg font-['Outfit']">{state.title}</DialogTitle>
        </DialogHeader>
        <p className="text-center text-white/70 text-sm leading-relaxed px-2" data-testid="confirm-dialog-desc">
          {state.description}
        </p>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <Button
            onClick={() => handleClose(false)}
            variant="outline"
            className="h-11 border-white/10 text-white/80 bg-white/5 rounded-xl"
            data-testid="confirm-dialog-cancel"
          >
            {state.cancelText}
          </Button>
          <Button
            onClick={() => handleClose(true)}
            className={`h-11 rounded-xl font-semibold ${state.danger ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-[#0ABAB5] text-black'}`}
            data-testid="confirm-dialog-confirm"
          >
            {state.confirmText}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [state]);

  return { ConfirmDialog, confirm };
}
